import React, { useEffect, useRef, useState } from 'react';
import { LauncherPanel } from './LauncherPanels';

export default function LauncherAdsPanel({ devices, adPlaylists = [], campaigns = [], socialPosts = [], status, message, blocked, busyKey, onLaunch }) {
  const [slot, setSlot] = useState('full');
  const [pending, setPending] = useState(null);
  const choiceRef = useRef(null);
  const entry = devices.find((device) => device.suitestreamApp && device.switchReady && !device.switchRequired)
    || devices.find((device) => device.suitestreamApp && device.switchReady);
  useEffect(() => {
    if (pending && (blocked || status !== 'ready' || pending.entry.target.id !== entry?.target.id
      || pending.entry.screen?.id !== entry?.screen?.id)) setPending(null);
  }, [blocked, status, entry?.target.id, entry?.screen?.id, pending]);
  useEffect(() => {
    if (!pending) return undefined;
    const previousFocus = document.activeElement;
    choiceRef.current?.focus();
    return () => previousFocus?.focus?.();
  }, [pending]);
  const confirm = (pauseBackground) => {
    if (!pending || blocked || status !== 'ready') return;
    setPending(null);
    onLaunch(pending.entry, { ...pending.item, pauseBackground });
  };
  const launchClass = !entry ? 'phone-remote__launch--unavailable'
    : entry.switchRequired ? 'phone-remote__launch--switch' : 'phone-remote__launch--local';
  const renderButton = (item, kind, available) => (
    <button key={item.id} type="button" className={`phone-remote__app-btn ${launchClass}`}
      disabled={blocked || !entry || !available || status !== 'ready'}
      onClick={() => setPending({ entry, item: { kind, id: item.id, slot: kind === 'campaign' ? slot : 'full', label: item.name } })}>
      <strong>{busyKey === `${entry?.target.id}:${kind}:${item.id}` ? 'Starting…' : item.name}</strong>
      <span>{!available ? kind === 'campaign' ? 'TV display not saved for this position' : 'Empty playlist'
        : !entry ? 'No compatible player on this screen'
          : `${entry.target.name} · ${entry.switchRequired ? 'Switch + display' : 'Display now'}`}</span>
    </button>
  );
  return (
    <LauncherPanel title="Ads" count={status === 'loading' ? 'Loading…' : `${adPlaylists.length + campaigns.length + socialPosts.length} in this site`}>
      <div className="phone-remote__notice">Stays on screen until Mix Now or another content selection.</div>
      <button type="button" className="phone-remote__shortcut-btn" disabled={blocked || !entry || status !== 'ready'}
        onClick={() => onLaunch(entry, { kind: 'mix_now', id: 'background', label: 'Background playback' })}>
        Mix Now · Return to background
      </button>
      {pending ? <div className="phone-remote__ad-dialog-backdrop">
        <div className="phone-remote__ad-dialog" role="dialog" aria-modal="true" aria-labelledby="launcher-pause-title"
          onKeyDown={(event) => {
            if (event.key === 'Escape') setPending(null);
            if (event.key === 'Tab') {
              const buttons = Array.from(event.currentTarget.querySelectorAll('button'));
              const index = buttons.indexOf(document.activeElement);
              event.preventDefault();
              buttons[(index + (event.shiftKey ? buttons.length - 1 : 1)) % buttons.length]?.focus();
            }
          }}>
          <strong id="launcher-pause-title">Pause Video?</strong>
          <p>{pending.item.label}</p>
          <button ref={choiceRef} type="button" className="phone-remote__shortcut-btn" disabled={blocked}
            onClick={() => confirm(true)}>Yes — fade out and pause</button>
          <button type="button" className="phone-remote__shortcut-btn" disabled={blocked}
            onClick={() => confirm(false)}>No — keep video playing</button>
          <button type="button" className="phone-remote__shortcut-btn" onClick={() => setPending(null)}>Cancel</button>
        </div>
      </div> : null}
      {message ? <div className="phone-remote__notice phone-remote__notice--error">{message}</div> : null}
      <LauncherPanel title="Ad Playlists" count={adPlaylists.length}>
        <div className="phone-remote__app-grid">{adPlaylists.map((item) => renderButton(item, 'ad_playlist', item.itemCount > 0))}</div>
        {status === 'ready' && !adPlaylists.length ? <div className="phone-remote__notice">No ad playlists saved in the scheduler.</div> : null}
      </LauncherPanel>
      <LauncherPanel title="Campaigns" count={campaigns.length}>
        <label className="phone-remote__ad-position">Display
          <select value={slot} onChange={(event) => setSlot(event.target.value)} disabled={blocked} aria-label="Campaign display position">
            <option value="full">Full (default)</option>
            <option value="top">Top</option>
            <option value="bottom">Bottom</option>
          </select>
        </label>
        <div className="phone-remote__app-grid">{campaigns.map((item) => renderButton(item, 'campaign', Boolean(item.slots?.[slot]) || item.availableSlots?.includes(slot)))}</div>
        {status === 'ready' && !campaigns.length ? <div className="phone-remote__notice">No saved campaigns in this site.</div> : null}
      </LauncherPanel>
      <LauncherPanel title="Social Content" count={socialPosts.length}>
        <div className="phone-remote__app-grid">{socialPosts.map((item) => renderButton(item, 'social_post', true))}</div>
        {status === 'ready' && !socialPosts.length ? <div className="phone-remote__notice">No presentation-ready social posts saved in this site.</div> : null}
      </LauncherPanel>
    </LauncherPanel>
  );
}
